import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/apiClient";
import type { BouquetTemplate } from "../api/types";
import { BouquetTemplateThumb } from "../components/BouquetTemplateThumb";

export function BouquetTemplatesPage() {
  const [rows, setRows] = useState<BouquetTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api.listBouquetTemplates());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load templates");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Bouquet templates</h2>
          <p className="page-subtitle">
            Preset ingredient lists for consistent designs. Add a photo to make each one easy to
            recognise on the build screen.
          </p>
        </div>
        <Link to="/app/bouquets/templates/new" className="button">
          New template
        </Link>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table>
          <thead>
            <tr>
              <th aria-label="Photo" style={{ width: 96 }} />
              <th>Name</th>
              <th>Description</th>
              <th style={{ width: 90, textAlign: "right" }}>Lines</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-state">
                  No templates yet.
                </td>
              </tr>
            ) : (
              rows.map((t) => (
                <tr key={t.id}>
                  <td>
                    <BouquetTemplateThumb
                      templateId={t.id}
                      imageKey={t.imageKey}
                      alt={t.name}
                      size={64}
                    />
                  </td>
                  <td>
                    <strong>{t.name}</strong>
                    {(["PAPER_REQUIREMENT", "RIBBON_REQUIREMENT"] as const).map((kind) => {
                      const swatches = t.lines
                        .filter((l) => l.kind === kind)
                        .flatMap((l) => l.acceptableColors ?? []);
                      if (swatches.length === 0) return null;
                      return (
                        <div
                          key={kind}
                          style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}
                        >
                          <span className="muted" style={{ fontSize: 12 }}>
                            {kind === "PAPER_REQUIREMENT" ? "Paper" : "Ribbon"}:
                          </span>
                          <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
                            {swatches.map((ac) => (
                              <span
                                key={ac.id}
                                title={ac.color.name}
                                style={{
                                  display: "inline-block",
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  border: "1px solid var(--color-border)",
                                  background: ac.color.hex ?? "transparent",
                                }}
                              />
                            ))}
                          </span>
                        </div>
                      );
                    })}
                  </td>
                  <td className="muted">{t.description ?? "—"}</td>
                  <td style={{ textAlign: "right" }}>{t.lines?.length ?? 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
